// src/components/ModalAutorizarOnu.jsx — dar de alta una ONU
//
// Dar de alta una ONU no es una operación: son cuatro llamadas seguidas a
// dos equipos —comprobar la serie, autorizar en la OLT, configurar la WAN y
// enganchar TR-069— y cada una puede fallar por su cuenta. Aquí SÍ se va en
// vivo: cada fase se marca cuando su llamada contesta, y lo que respondió
// el equipo se reproduce dentro de su fase.
//
// La animación de la fibra se queda como estaba: es lo que funcionaba.
//
// Lo que cambió además del diseño: antes, si algo fallaba, el velo se
// cerraba solo y nunca se veía qué paso se había roto; y al terminar bien
// se cerraba sin enseñar el resultado. Ahora se queda hasta que se cierra,
// y la lista de ONUs se actualiza al cerrar.

import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./ModalAutorizarOnu.css";
import { authFetch } from "../utils/authFetch";
import { Boton, Iconos } from "../componentes/base";
import ModalPasos from "../componentes/olt/ModalPasos";

const FASES_INICIALES = [
  { clave: "serial", rotulo: "Comprobar la serie", detalle: "que no esté ya dada de alta", estado: "pendiente" },
  { clave: "olt", rotulo: "Autorizar en la OLT", detalle: "perfiles GLOBALes y service-port", estado: "pendiente" },
  { clave: "wan", rotulo: "Configurar la WAN", detalle: "PPPoE, VLAN y rutas", estado: "pendiente" },
  { clave: "gestion", rotulo: "Gestión remota", detalle: "acceso web y TR-069", estado: "pendiente" },
  { clave: "fin", rotulo: "En servicio", detalle: "la ONT puede navegar", estado: "pendiente" },
];

/* La OLT hablando con ESTA ONU, y con ninguna otra.

   Es la misma fibra de la pantalla de potencias, reducida al ÚNICO hilo
   que importa: al final cuelga la ONT que se está dando de alta. La luz
   va y vuelve porque autorizar es una conversación —la OLT pregunta, la
   ONT contesta con su serial—, no un envío. */
function FibraOnu({ trabajando, listo, roto }) {
  return (
    <svg
      viewBox="0 0 320 60"
      className={
        "fonu" +
        (trabajando ? " fonu--viva" : "") +
        (listo ? " fonu--lista" : "") +
        (roto ? " fonu--rota" : "")
      }
      aria-hidden="true"
    >
      <rect x="4" y="18" width="40" height="24" rx="5" className="fonu__caja" />
      <text x="24" y="34" className="fonu__txt">OLT</text>

      <path d="M 44 30 H 132" className="fonu__hilo" pathLength="100" />
      <path d="M 44 30 H 132" className="fonu__luz fonu__luz--ida" pathLength="100" />

      <rect x="132" y="20" width="13" height="20" rx="3" className="fonu__caja" />

      {/* Un solo hilo. Antes salían tres del divisor —dos apagados, para
          decir «ésta es una de muchas»—, pero lo que se está haciendo
          afecta a UNA ONT, y los otros dos brazos no contaban nada que
          hiciera falta mirar mientras se espera. */}
      <path d="M 145 30 H 262" className="fonu__hilo" pathLength="100" />
      <path d="M 145 30 H 262" className="fonu__luz fonu__luz--rama" pathLength="100" />
      <path d="M 262 30 H 145" className="fonu__luz fonu__luz--vuelta" pathLength="100" />

      {/* La ONU */}
      <rect x="262" y="18" width="46" height="24" rx="5" className="fonu__caja fonu__onu" />
      <text x="285" y="34" className="fonu__txt">ONU</text>
    </svg>
  );
}

export default function ModalAutorizarOnu({ onu, oltId, onClose, onSuccess }) {
  const [clienteId, setClienteId] = useState("");
  const [cliente, setCliente] = useState(null);
  const [errorForm, setErrorForm] = useState("");
  const [buscando, setBuscando] = useState(false);

  const [estado, setEstado] = useState("reposo");
  const [fases, setFases] = useState(FASES_INICIALES);
  const [salidas, setSalidas] = useState({ olt: [], wan: [], gestion: [] });
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  const [duplicadaInfo, setDuplicadaInfo] = useState(null);

  const marcar = (clave, estadoFase, dato) =>
    setFases((fs) => fs.map((f) => (f.clave === clave ? { ...f, estado: estadoFase, dato: dato ?? f.dato } : f)));

  /* ---------- buscar al cliente ---------- */
  async function buscarCliente() {
    setErrorForm(""); setCliente(null);
    if (!clienteId.trim()) { setErrorForm("Escribe el número de cliente."); return; }
    try {
      setBuscando(true);
      const res = await authFetch(`/api/clientes/buscar/${clienteId}`);
      const data = await res.json();
      if (!data.ok) setErrorForm("No hay ningún cliente con ese número.");
      else setCliente(data.cliente);
    } catch {
      setErrorForm("No se pudo consultar al servidor.");
    } finally {
      setBuscando(false);
    }
  }

  /* ---------- el alta ---------- */
  function fallar(clave, msg) {
    // El mensaje va solo en el aviso de abajo; la fase ya se pinta en rojo
    // y repetirlo debajo de su título lo decía dos veces.
    marcar(clave, "mal");
    // Lo que no se llegó a hacer, tachado: que se vea que no pasó.
    setFases((fs) => {
      const i = fs.findIndex((f) => f.clave === clave);
      return fs.map((f, n) => (n > i ? { ...f, estado: "cancelado" } : f));
    });
    setError(msg);
    setEstado("fin");
  }

  async function autorizar() {
    if (!cliente) return;
    setFases(FASES_INICIALES); setSalidas({ olt: [], wan: [], gestion: [] });
    setError(""); setResultado(null);
    setEstado("corriendo");

    // 1) La serie no puede estar dada de alta ya.
    marcar("serial", "activo");
    try {
      const res = await authFetch("/api/olt/onu/check_duplicada", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serial: onu.sn }),
      });
      const data = await res.json();
      if (data.ok && data.duplicada) {
        setDuplicadaInfo(data);
        setFases(FASES_INICIALES);
        setEstado("reposo");
        return;
      }
      marcar("serial", "hecho", onu.sn);
    } catch {
      fallar("serial", "No se pudo comprobar si la serie ya está dada de alta.");
      return;
    }

    // 2) Autorizar en la OLT y guardar en la base.
    const [frame = 0, slot = 0, pon = 0] = (onu.puerto || "").split("/").map(Number);
    let alta = null;
    marcar("olt", "activo");
    try {
      const res = await authFetch("/api/olt/autorizar_onu", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          olt_id: oltId, cliente_id: cliente.id, serial: onu.sn, modelo: onu.model,
          frame, slot, pon, plan_id: cliente.plan, pppoe_user: cliente.pppoe_user, mac: onu.mac,
        }),
      });
      alta = await res.json().catch(() => ({}));
      setSalidas((s) => ({ ...s, olt: Array.isArray(alta?.outputs) ? alta.outputs : [] }));
      if (!res.ok || !alta.ok) { fallar("olt", alta?.mensaje || "La OLT no autorizó la ONU."); return; }
      if (!alta.onu_db_id) { fallar("olt", "La ONU se autorizó pero el servidor no devolvió su número en la base."); return; }
      marcar("olt", "hecho", `ONT ${alta.onu_id ?? "?"} · service-port ${alta.service_port ?? "?"}`);
    } catch {
      fallar("olt", "Se perdió la conexión con el servidor al autorizar.");
      return;
    }

    // 3) WAN.
    let wan = null;
    marcar("wan", "activo");
    try {
      const res = await authFetch(`/api/onus/${alta.onu_db_id}/wan_apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // El acceso web va en el paso siguiente: justo después de la WAN la
        // ONT se reconfigura y la OLT lo rechazaría.
        body: JSON.stringify({ ip_index: 0, enable_http: false }),
      });
      wan = await res.json().catch(() => ({}));
      setSalidas((s) => ({ ...s, wan: Array.isArray(wan?.outputs) ? wan.outputs : [] }));
      if (!res.ok || !wan?.ok) { fallar("wan", wan?.mensaje || "No se pudo configurar la WAN."); return; }
      marcar("wan", "hecho");
    } catch (e) {
      fallar("wan", String(e?.message || e || "Error configurando la WAN."));
      return;
    }

    // 4) Gestión remota: acceso web y TR-069. Van al final porque la ONT
    //    recién configurada tarda unos segundos en aceptarlos; el backend
    //    reintenta. Lo que no entre queda PENDIENTE sin tumbar el alta: a
    //    estas alturas la ONT ya navega.
    let gestion = null;
    marcar("gestion", "activo");
    try {
      const res = await authFetch(`/api/onus/${alta.onu_db_id}/gestion_remota`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ http: true }),
      });
      gestion = await res.json().catch(() => ({}));
      setSalidas((s) => ({ ...s, gestion: Array.isArray(gestion?.outputs) ? gestion.outputs : [] }));
      if (!res.ok || !gestion?.ok) { fallar("gestion", gestion?.mensaje || "No se pudo configurar la gestión remota."); return; }
      const pend = gestion.pendiente || [];
      marcar("gestion", "hecho", [
        (gestion.hecho || []).join(" y "),
        pend.length ? `pendiente: ${pend.map((x) => x.que).join(" y ")}` : "",
        gestion.tr069 === false ? "TR-069 no aplica en esta OLT" : "",
      ].filter(Boolean).join(" · "));
    } catch (e) {
      fallar("gestion", String(e?.message || e || "Error en la gestión remota."));
      return;
    }

    marcar("fin", "hecho");
    setResultado({ ...alta, wan, gestion });
    setEstado("fin");
  }

  // Al cerrar tras un alta buena se avisa a la lista: es la que quita la
  // ONU de las no autorizadas y cierra el modal.
  const cerrar = () => {
    if (estado === "corriendo") return;
    if (resultado) onSuccess?.(resultado);
    else onClose?.();
  };

  const hayError = fases.some((f) => f.estado === "mal");
  const terminado = estado === "fin" && !hayError;

  return (
    <>
      <ModalPasos
        abierto
        titulo="Dar de alta la ONU"
        icono={Iconos.agregar}
        ficha={[
          ["Serie", onu.sn || "—", true],
          ["Modelo", onu.model || "—"],
          ["Puerto", onu.puerto || "—", true],
          ["MAC", onu.mac || "—", true],
        ]}
        arriba={<FibraOnu trabajando={estado === "corriendo"} listo={terminado} roto={hayError} />}
        verbo="Dar de alta"
        listo={Boolean(cliente) && !buscando}
        estado={estado}
        error={error}
        mensaje={resultado
          ? `ONU en servicio: ONT ${resultado.onu_id ?? "?"} del ${onu.puerto}, service-port ${resultado.service_port ?? "?"}.`
            /* El motivo lo manda el backend en `detalle`: es la línea que
               contestó la OLT. Antes aquí se afirmaba que la ONT había
               tardado, siempre, sin mirarla — y cuando el error era otro
               (un "% Unknown command", por ejemplo) mandaba a reintentar
               algo que no iba a funcionar nunca. */
            + (resultado.gestion?.pendiente?.length
              ? ` Quedó pendiente ${resultado.gestion.pendiente
                  .map((x) => (x.detalle ? `${x.que} — ${x.detalle}` : x.que))
                  .join("; ")}. Reinténtalo desde la ONU.`
              : "")
          : "ONU en servicio."}
        tono={resultado?.gestion?.pendiente?.length ? "aviso" : "bien"}
        fases={fases.map((f) => (
          f.clave === "wan" || f.clave === "gestion" || (f.clave === "olt" && salidas.olt.length)
            ? { ...f, salida: salidas[f.clave] }
            : { ...f, app: true }
        ))}
        onEjecutar={autorizar}
        onCerrar={cerrar}
      >
        <span className="mp-rotulo">Cliente</span>
        <div className="mp-campos" style={{ gridTemplateColumns: "1fr auto" }}>
          <label className="mp-campo">
            <span>Número de cliente</span>
            <input
              type="number"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") buscarCliente(); }}
              placeholder="Ej. 2801"
              disabled={buscando}
            />
          </label>
          <div style={{ alignSelf: "end" }}>
            <Boton icono={Iconos.buscar} cargando={buscando} onClick={buscarCliente}>Buscar</Boton>
          </div>
        </div>

        {errorForm && (
          <div className="mp-aviso mp-aviso--peligro"><Iconos.error size={16} strokeWidth={2} /><span>{errorForm}</span></div>
        )}

        {cliente && (
          <dl className="mp-res">
            <div><dt>Cliente</dt><dd>#{cliente.id}</dd></div>
            <div><dt>Nombre</dt><dd>{[cliente.nombre, cliente.apellidos].filter(Boolean).join(" ") || "—"}</dd></div>
            <div><dt>Plan</dt><dd>{cliente.plan || "—"}</dd></div>
            <div><dt>PPPoE</dt><dd>{cliente.pppoe_user || "—"}</dd></div>
            <div className={cliente.num_serie ? "mp-res--aviso" : undefined}>
              <dt>ONU actual</dt><dd>{cliente.num_serie || "ninguna"}</dd>
            </div>
          </dl>
        )}
      </ModalPasos>

      {duplicadaInfo && createPortal(
        <div className="modal-duplicada-overlay">
          <div className="modal-duplicada">
            <h3>Esta ONU ya está dada de alta</h3>
            <p>La serie <strong>{onu.sn}</strong> ya está registrada.</p>
            {duplicadaInfo.cliente ? (
              <div className="duplicada-info">
                <p>
                  <strong>Cliente:</strong> {duplicadaInfo.cliente.id} —{" "}
                  {duplicadaInfo.cliente.nombre} {duplicadaInfo.cliente.apellidos}
                </p>
                <p>
                  <strong>Puerto:</strong> {duplicadaInfo.onu?.fsp} (ONT {duplicadaInfo.onu?.ont_id})
                </p>
              </div>
            ) : (
              <p>Está registrada pero sin cliente asociado.</p>
            )}
            <p className="duplicada-warning">
              Para darla de alta aquí, primero hay que eliminarla donde está.
            </p>
            <div className="duplicada-buttons">
              <Boton variante="secundario" icono={Iconos.cerrar} onClick={() => setDuplicadaInfo(null)}>
                Entendido
              </Boton>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
